import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computed } from 'mobx';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { AttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import { lazyGetAttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import {
    constructKeytip,
    createAppFlyoutAnchor,
    createAppSplitButton,
    getKeytipString,
} from 'owa-command-ribbon';
import {
    getDefaultSlrAppRibbonButtonStyles,
    getSlrHeroAppSplitButtonStyles,
} from 'owa-command-ribbon-styles';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { focusToEditor } from 'owa-editor';
import { lazyCloseChecker, lazyOpenChecker } from 'owa-editor-acc-checker';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import toggleFrom from 'owa-mail-compose-actions/lib/actions/toggleFrom';
import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import { discard, send, showMessageOptionsDialog, switchBodyType } from 'owa-mail-compose-command';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type AttachmentMultiPicker from 'owa-mail-compose-view/lib/types/AttachmentMultiPicker';
import isApprovalResponse from 'owa-mail-compose-view/lib/utils/isApprovalResponse';
import { getMailMenuItemShouldShowMap, MenuItemType } from 'owa-mail-filterable-menu-behavior';
import { ControlInfo, getControlInfo, RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId } from 'owa-ribbon-ids';
import { getPalette } from 'owa-theme';
import {
    onNewEmailSplitButtonExecuted,
    onNewMessage,
    onNewNoteClicked,
    onRibbonCustomizerClicked,
    onSaveDraft,
    onToggleFolderPane,
} from '../../../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppToggleButton,
} from '../../../../util/createMailRibbonButton';
import { getToolbarRibbonIconColor } from '../../../../util/getToolbarRibbonIconColor';
import {
    AttachmentFlyoutSections,
    getAttachFileSections,
    getEncryptSingleButton,
    getImportanceSections,
    getNewGroupSections,
    getProtectEncryptSections,
    getSignatureSections,
    getSensitivitySections,
} from '../../../sections';
import { getSensitivityMenu } from 'owa-compose-actioncontrols/lib/components/controls/sensitivityMenu';

/**
 * This file contains the definitions for all the controls
 * in the compose toolbar ribbon
 */
export const getLeftToggleControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ToggleLeftPane);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.ToggleLeftPane, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            onToggleFolderPane,
            undefined /* keytipProps */,
            controlInfo.iconName
        );
    }
);

export const getNewMessageControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();
        const controlInfo = getControlInfo(MailRibbonControlId.NewMessageSplitButton);
        const isNote = shouldShowBehaviorMap[MenuItemType.Ribbon_NewNote]();

        // This should really be calling createMailAppSplitButton(),
        // but there is a WAC bug which overwrites styles rather than
        // merging them. Fortunately, we don't REALLY need the merge in this case.
        return createAppSplitButton(
            getComposeRibbonId(MailRibbonControlId.NewMessageSplitButton, popoutComposeViewState),
            controlInfo.text,
            {
                sections: [
                    {
                        controls: getNewGroupSections(undefined),
                    },
                ],
            },
            isNote ? onNewNoteClicked : onNewMessage,
            isFeatureEnabled('mon-ribbon-keytips')
                ? constructKeytip(
                      [],
                      getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                      true /* hasMenu */
                  )
                : undefined,
            controlInfo.iconName,
            getPalette().white,
            getSlrHeroAppSplitButtonStyles(true /*isToolbarComposeStyle*/),
            {
                customTooltip: isFeatureEnabled('mon-ribbon-tooltips')
                    ? controlInfo.tooltip
                    : undefined,
                onMenuButtonExecute: onNewEmailSplitButtonExecuted,
                primaryDisabled:
                    !shouldShowBehaviorMap[MenuItemType.Ribbon_NewMessage]() &&
                    !shouldShowBehaviorMap[MenuItemType.Ribbon_NewNote](),
            }
        );
    }
);

export const getAttachFileControl = computedFn(
    (
        attachmentPickers: AttachmentMultiPicker,
        popoutComposeViewState: ComposeViewState | undefined
    ): RibbonControlProps => {
        // Grab suggestionsGetter. Note that this isn't undefined, as getAttachFileControl only is provoked if
        // shouldShowRibbonControl returns true for AttachFile, which already checks that suggestionsGetter != undefined.
        const suggestionsGetter: AttachmentSuggestionsMenuItems = lazyGetAttachmentSuggestionsMenuItems.tryImportForRender()?.(
            true /* isMail */
        )!;
        const controlInfo = getControlInfo(MailRibbonControlId.AttachFile);
        const attachmentSections: AttachmentFlyoutSections = getAttachFileSections(
            suggestionsGetter,
            attachmentPickers,
            undefined,
            popoutComposeViewState
        );

        const viewState: ComposeViewState | undefined =
            popoutComposeViewState ?? getMainWindowComposeViewState();

        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.AttachFile, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            { sections: attachmentSections.menuSections },
            constructKeytip(
                [],
                getKeytipString(MailRibbonControlId.AttachFile),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            undefined /*Styles*/,
            {
                onExecute: attachmentSections.onMenuOpened,

                onAfterMenuDismiss: attachmentSections.onMenuDismissed,

                disabled:
                    attachmentSections.menuSections.length === 0 ||
                    !viewState ||
                    isApprovalResponse(viewState),
            }
        );
    }
);

export const getAttachSigControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const signatureSections: MenuSection[] = getSignatureSections(
            undefined,
            popoutComposeViewState
        );
        const controlInfo = getControlInfo(MailRibbonControlId.AttachSig);
        return createAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.AttachSig, popoutComposeViewState),
            controlInfo.text,
            {
                sections: signatureSections,
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.AttachSig), true /* hasMenu */),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultRibbonStyles() /*Styles*/,
            {
                customTooltip: isFeatureEnabled('mon-ribbon-tooltips')
                    ? controlInfo.tooltip
                    : undefined,
                disabled: signatureSections[0].controls.length == 0,
            }
        );
    }
);

export const getShowHideFromControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const viewState: ComposeViewState =
            popoutComposeViewState ?? getMainWindowComposeViewState()!;
        const controlInfo: ControlInfo = getControlInfo(MailRibbonControlId.ShowHideFrom);
        return createMailAppToggleButton(
            getComposeRibbonId(MailRibbonControlId.ShowHideFrom, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                const viewState: ComposeViewState =
                    popoutComposeViewState ?? getMainWindowComposeViewState()!;

                toggleFrom(viewState);
                focusToEditor(viewState);
            },
            viewState ? viewState.fromViewState.isFromShown : false,
            constructKeytip([], getKeytipString(MailRibbonControlId.ShowHideFrom)),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

export const getDiscardControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.DiscardDraft);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.DiscardDraft, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                discard(popoutComposeViewState ?? getMainWindowComposeViewState()!, targetWindow);
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.DiscardDraft)),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

export const getSendControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.SendDraft);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.SendDraft, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                send(popoutComposeViewState ?? getMainWindowComposeViewState()!, targetWindow);
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.SendDraft)),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

export const getSensitivityControl = computedFn(
    (props: RibbonControlDefinitionProps): RibbonControlProps => {
        const keyTips = constructKeytip(
            [],
            getKeytipString(MailRibbonControlId.Sensitivity),
            true /* hasMenu */
        );
        const viewState = props.popoutComposeViewState ?? getMainWindowComposeViewState();
        const menu = { sections: getSensitivitySections(props) };
        return getSensitivityMenu(viewState, menu, keyTips, undefined);
    }
);

export const getShowMessageOptionsControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ShowMessageOptions);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.ShowMessageOptions, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                showMessageOptionsDialog(
                    popoutComposeViewState ?? getMainWindowComposeViewState()!,
                    targetWindow
                );
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.ShowMessageOptions)),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

export const getSwitchDraftFormatControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(
            MailRibbonControlId.SwitchDraftFormat,
            popoutComposeViewState ?? getMainWindowComposeViewState()!
        );

        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.SwitchDraftFormat, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                switchBodyType(
                    popoutComposeViewState ?? getMainWindowComposeViewState()!,
                    targetWindow
                );
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.SwitchDraftFormat)),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

function appcheckerFunctions(viewState: ComposeViewState, targetWindow: Window) {
    const { accChecker } = viewState;

    if (accChecker.isOpen) {
        lazyCloseChecker.import().then(closeChecker => closeChecker(accChecker, targetWindow));
    } else {
        lazyOpenChecker
            .import()
            .then(openChecker => openChecker(accChecker, viewState, targetWindow));
    }
}

export const getCheckForAccessibilityIssuesControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.CheckForAccessibilityIssues);
        const viewState: ComposeViewState =
            popoutComposeViewState ?? getMainWindowComposeViewState()!;

        return createMailAppButton(
            getComposeRibbonId(
                MailRibbonControlId.CheckForAccessibilityIssues,
                popoutComposeViewState
            ),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                appcheckerFunctions(viewState, targetWindow);
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.CheckForAccessibilityIssues)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            undefined /*Styles*/,
            { disabled: viewState.bodyType != 'HTML' }
        );
    }
);

export const getEncryptControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Encrypt);
        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.Encrypt, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            {
                sections: [
                    {
                        title: loc(Strings.encrypt_FlyoutTitle),
                        controls: getProtectEncryptSections(
                            popoutComposeViewState ?? getMainWindowComposeViewState()!,
                            targetWindow
                        ),
                    },
                ],
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.Encrypt), true /* hasMenu */),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

export const getEncryptSingleButtonControl = computedFn(
    (
        popoutComposeViewState: ComposeViewState | undefined,
        targetWindow: Window
    ): RibbonControlProps => {
        return getEncryptSingleButton([], popoutComposeViewState, targetWindow);
    }
);

export const getSetImportanceControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.SetImportance);
        return createMailAppFlyoutAnchor(
            getComposeRibbonId(MailRibbonControlId.SetImportance, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            {
                sections: getImportanceSections(popoutComposeViewState),
            },
            constructKeytip(
                [],
                getKeytipString(MailRibbonControlId.SetImportance),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

export const getSaveControl = computedFn(
    (popoutComposeViewState: ComposeViewState | undefined): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Save);
        return createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.Save, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                onSaveDraft(popoutComposeViewState ?? getMainWindowComposeViewState()!);
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.Save)),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
        );
    }
);

const computeRibbonCustomizerControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RibbonCustomizer);
        return createMailAppButton(
            MailRibbonControlId.RibbonCustomizer,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            () => {
                onRibbonCustomizerClicked();
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.RibbonCustomizer)),
            undefined /* icon */,
            undefined /* iconColor */,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getRibbonCustomizerControl = () => computeRibbonCustomizerControl.get();
